import { useEffect, useMemo } from 'react';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { getExperienceInitCookie } from './experienceInitCookie';
import { clearUserSessionTimers, useActivateUserSessionTimers } from './userSessionTimers';
import { SessionStore } from '../../constants/sessionStore';
import { useMockApi } from '../hooks/useMockApi';
import { User } from '../../store/interfaces';

const axiosInstance = axios.create({ withCredentials: true });

export const useAxiosInterceptor = (candidate: User): void => {
  const activateUserSessionTimers = useActivateUserSessionTimers();

  useMockApi(axiosInstance);

  // create the interceptors
  const interceptors = useMemo(
    () => ({
      request: (config: AxiosRequestConfig): AxiosRequestConfig => {
        const userSessionActive = sessionStorage.getItem(SessionStore.userSessionActive);

        if (userSessionActive && JSON.parse(userSessionActive)) {
          clearUserSessionTimers();
          activateUserSessionTimers();
        }

        const experienceInitCookie = getExperienceInitCookie();
        if (experienceInitCookie) config.headers['X-CSRF-TOKEN'] = experienceInitCookie.csrfToken;

        return config;
      },
      response: (response: AxiosResponse): AxiosResponse => response,
      error: (error: AxiosError): Promise<never> => Promise.reject(error.response),
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  useEffect(() => {
    /** Intercepting request to send csrf in request headers */
    axiosInstance.interceptors.request.use(interceptors.request);
    /** Intercepting response */
    axiosInstance.interceptors.response.use(interceptors.response, interceptors.error);
  }, [interceptors]);
};

export default axiosInstance;
